/**
 * useCanvasCollaboration — real-time multi-user sync for a canvas board.
 *
 * Mirrors the Newsletter collab hook (Yjs over a custom Socket.IO namespace),
 * bound to Fabric's serialised scene JSON stored under a single Y.Map key
 * ('json'). Each committed local change (from CanvasCore's onChange) overwrites
 * that key; peers reload the scene via canvasRef.applyRemoteJSON — which itself
 * skips while the local user is mid-drag or editing text, so no one's work is
 * yanked out from under them.
 *
 * Returns { active, users, broadcastLocal, sendCursor, commentsTick,
 * announceComments } for the editor to wire.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { io } from 'socket.io-client';
import { API_URL } from '../../../api/api';

const COLLAB_URL = API_URL || '';
const DOC_KEY = 'json';

export function useCanvasCollaboration(canvasId, canvasRef) {
  const [active, setActive] = useState(false);
  const [users, setUsers] = useState([]);
  // Bumped when a peer changes the board's comments — the comments layer
  // refetches on change.
  const [commentsTick, setCommentsTick] = useState(0);
  const socketRef = useRef(null);
  const ydocRef = useRef(null);
  const applyingRemote = useRef(false); // guard: don't rebroadcast a remote apply
  const lastCursor = useRef(0);

  useEffect(() => {
    if (!canvasId) return undefined;

    const ydoc = new Y.Doc();
    ydocRef.current = ydoc;
    const ymap = ydoc.getMap('canvas');

    let userData = {};
    try { userData = JSON.parse(localStorage.getItem('userdata') || '{}'); } catch { /* ignore */ }
    const user = { id: userData.id, username: userData.username || userData.name || 'User' };

    const socket = io(`${COLLAB_URL}/canvas-collab`, {
      auth: { token: localStorage.getItem('token') },
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socketRef.current = socket;

    const applyFromYjs = async () => {
      const json = ymap.get(DOC_KEY);
      if (!json) return;
      applyingRemote.current = true;
      try { await canvasRef.current?.applyRemoteJSON(json); }
      finally { applyingRemote.current = false; }
    };

    socket.on('connect', () => {
      setActive(true);
      socket.emit('join_room', { canvasId, user });
    });
    socket.on('room_state', (encoded) => {
      Y.applyUpdate(ydoc, new Uint8Array(encoded));
      applyFromYjs();
    });
    socket.on('yjs_update', (update) => {
      Y.applyUpdate(ydoc, new Uint8Array(update));
      applyFromYjs();
    });
    socket.on('comments_changed', () => setCommentsTick((n) => n + 1));
    socket.on('presence_update', (list) => {
      setUsers((list || []).filter((u) => u.socketId !== socket.id));
    });
    socket.on('disconnect', () => setActive(false));
    socket.on('connect_error', (err) => { console.warn('[canvas-collab] connect error:', err.message); setActive(false); });

    // Relay local Yjs deltas to the server.
    const onDocUpdate = (update) => {
      socket.emit('yjs_update', { canvasId, update: Array.from(update) });
    };
    ydoc.on('update', onDocUpdate);

    return () => {
      ydoc.off('update', onDocUpdate);
      socket.emit('leave_room', { canvasId });
      socket.disconnect();
      ydoc.destroy();
      setActive(false);
      setUsers([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasId]);

  // Push the board's current JSON into the shared doc (called from CanvasCore.onChange).
  const broadcastLocal = useCallback((json) => {
    if (applyingRemote.current || !ydocRef.current) return;
    const ymap = ydocRef.current.getMap('canvas');
    if (ymap.get(DOC_KEY) === json) return;
    ydocRef.current.transact(() => ymap.set(DOC_KEY, json));
  }, []);

  // Throttled cursor position broadcast (scene coords).
  const sendCursor = useCallback((cursor) => {
    const now = Date.now();
    if (now - lastCursor.current < 60) return;
    lastCursor.current = now;
    socketRef.current?.emit('cursor_update', { canvasId, cursor });
  }, [canvasId]);

  // Tell peers this board's comments changed (after a REST write) so they refetch.
  const announceComments = useCallback(() => {
    socketRef.current?.emit('comments_changed', { canvasId });
  }, [canvasId]);

  return { active, users, broadcastLocal, sendCursor, commentsTick, announceComments };
}
